import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from 'modules/views/shared/Header';
import { useStyles } from './styles';
import { MyProfileHeader } from './components/ProjectHeader';
import {
  ProfileTabs,
  ProfileTabIndex,
  ProfileInformation,
  LoginInformation,
  ChangePassword,
  CreatePassword,
} from './components';
import useLoadAllData from 'hooks/useLoadAllData';
import { useAppSelector, useAppDispatch } from 'modules/state-mgmt/store';
import { resetAdminContacts } from 'modules/state-mgmt/workerData';
import { mustCreateWorkerPassword } from 'utils/onboarding';

const TAB_PARAM = 'tab';

const TAB_TO_PARAM: Record<ProfileTabIndex, string> = {
  [ProfileTabIndex.PROFILE_INFORMATION]: 'profile',
  [ProfileTabIndex.LOGIN_INFORMATION]: 'login',
  [ProfileTabIndex.CHANGE_PASSWORD]: 'password',
};

const PARAM_TO_TAB: Record<string, ProfileTabIndex> = {
  profile: ProfileTabIndex.PROFILE_INFORMATION,
  login: ProfileTabIndex.LOGIN_INFORMATION,
  password: ProfileTabIndex.CHANGE_PASSWORD,
};

const getInitialTab = (
  tabParam: string | null,
  isAdminSessionContext: boolean,
  hasNoPassword?: boolean,
): ProfileTabIndex => {
  // A worker without a password only has the Create Password tab (CH-4515).
  if (hasNoPassword) {
    return ProfileTabIndex.CHANGE_PASSWORD;
  }
  if (tabParam && tabParam in PARAM_TO_TAB) {
    const tabFromUrl = PARAM_TO_TAB[tabParam];
    // Admin context: profile tab is hidden — fall back to login
    if (isAdminSessionContext && tabFromUrl === ProfileTabIndex.PROFILE_INFORMATION) {
      return ProfileTabIndex.LOGIN_INFORMATION;
    }
    return tabFromUrl;
  }
  return isAdminSessionContext ? ProfileTabIndex.LOGIN_INFORMATION : ProfileTabIndex.PROFILE_INFORMATION;
};

const MyProfile = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isWorker, onboardingUserId, hasNoPassword } = useAppSelector(state => ({
    isWorker: state.user?.isWorker,
    onboardingUserId: state.onboarding?.userId,
    // A worker who signed in with a one-time password and never set one is locked to the
    // Create Password tab until they do (CH-4515).
    hasNoPassword: mustCreateWorkerPassword(state.onboarding),
  }));

  // URL param: AdminMenu passes ?profile=admin so the admin's own profile view is explicit
  // and survives page refresh.
  // Fallback: !isWorker && !onboardingUserId catches a pure admin session where no worker
  // has been loaded yet.
  const isAdminSessionContext = searchParams.get('profile') === 'admin' || (!isWorker && !onboardingUserId);

  const [activeTab, setActiveTab] = useState<number>(() =>
    getInitialTab(searchParams.get(TAB_PARAM), isAdminSessionContext, hasNoPassword),
  );
  // Gate LoginInformation render in admin context until adminContacts is cleared,
  // so it always mounts with empty data (no stale flash from a previous admin account).
  const [adminContactsReady, setAdminContactsReady] = useState(!isAdminSessionContext);
  const [, loadAllData] = useLoadAllData();
  const workerId = useAppSelector(state => state.onboarding.id);

  useEffect(() => {
    loadAllData({ workerId, skipProjectApis: true, skipWorkerApis: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev.toString());
        next.set(TAB_PARAM, TAB_TO_PARAM[activeTab as ProfileTabIndex]);
        return next;
      },
      { replace: true },
    );
  }, [activeTab, setSearchParams]);

  useEffect(() => {
    // `hasNoPassword` can flip to true once the worker details load, so pin the tab here too
    // rather than letting the default reset move them off Create Password.
    setActiveTab(
      hasNoPassword
        ? ProfileTabIndex.CHANGE_PASSWORD
        : isAdminSessionContext
          ? ProfileTabIndex.LOGIN_INFORMATION
          : ProfileTabIndex.PROFILE_INFORMATION,
    );
    if (isAdminSessionContext) {
      dispatch(resetAdminContacts());
      setAdminContactsReady(true);
    }
  }, [isAdminSessionContext, hasNoPassword, dispatch]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <div className={classes.wrapper}>
      <div className={`${classes.container} ${!isWorker ? classes.containerWithWarning : ''}`}>
        <Header styleClass={classes.mainHeader} showOnlineWarning={!isWorker} />
        <MyProfileHeader />
        <ProfileTabs
          activeTab={activeTab}
          onTabChange={handleTabChange}
          hideProfileInformation={isAdminSessionContext}
          hasNoPassword={hasNoPassword}
        />
        {!hasNoPassword && !isAdminSessionContext && activeTab === ProfileTabIndex.PROFILE_INFORMATION && (
          <div className={classes.tabPanel} data-testid="my-profile-panel-profile-information">
            <ProfileInformation />
          </div>
        )}
        {!hasNoPassword && activeTab === ProfileTabIndex.LOGIN_INFORMATION && adminContactsReady && (
          <div className={classes.tabPanel} data-testid="my-profile-panel-login-information">
            <LoginInformation isAdminSessionContext={isAdminSessionContext} />
          </div>
        )}
        {activeTab === ProfileTabIndex.CHANGE_PASSWORD && (
          <div className={classes.tabPanel} data-testid="my-profile-panel-change-password">
            {hasNoPassword ? <CreatePassword /> : <ChangePassword isAdminSessionContext={isAdminSessionContext} />}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyProfile;
